import { useCallback, useContext, useRef, useState, type ReactElement } from 'react'
import { AppDialog, Textarea, type AppDialogAction } from '../Common'
import { FilePicker, type FileReject } from '../Common/FilePicker'
import { FileRow } from '../Common/FileRow'
import { STORAGE_MAX_FILE_BYTES } from '../../services/storageHubService'
import { uploadHomeworkFiles, submitEduHomework } from '../../services/eduHomeworkService'
import { isTransientOutboxError, queueMemberHomeworkSubmit } from '../../services/memberOutboxService'
import { AuthContext } from '../../context/authContextCore'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import './familyHandInDialog.css'

export interface FamilyHandInDialogProps {
  open: boolean
  workspaceId: string
  checklistId: string
  learnerId: string
  title?: string
  onClose: () => void
  onSuccess: () => void
}

export const FamilyHandInDialog = ({
  open,
  workspaceId,
  checklistId,
  learnerId,
  title,
  onClose,
  onSuccess,
}: FamilyHandInDialogProps): ReactElement | null => {
  const { t } = useNls()
  const auth = useContext(AuthContext)
  const currentUser = auth?.currentUser
  const [note, setNote] = useState<string>('')
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [savedOffline, setSavedOffline] = useState<boolean>(false)
  const [rejectErrors, setRejectErrors] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number } | null>(null)
  const uploadedFileIdsRef = useRef<Map<File, string>>(new Map())

  const handleReset = useCallback(() => {
    setNote('')
    setFiles([])
    uploadedFileIdsRef.current.clear()
    setRejectErrors([])
    setError(null)
    setIsSubmitting(false)
    setUploadProgress(null)
  }, [])


  const handleCancel = useCallback(() => {
    if (isSubmitting) return
    handleReset()
    onClose()
  }, [isSubmitting, handleReset, onClose])

  const handleFiles = useCallback(
    (newFiles: File[]) => {
      setError(null)
      const combined = [...files, ...newFiles]
      if (combined.length > 5) {
        setError(t('NLSFamilyHandInDialogTooManyFiles'))
        setFiles(combined.slice(0, 5))
      } else {
        setFiles(combined)
      }
    },
    [files, t],
  )

  const handleReject = useCallback(
    (rejects: FileReject[]) => {
      if (rejects.length === 0) return
      const messages = rejects.map((r) =>
        r.reason === 'size'
          ? t('NLSCommonFilePickerRejectSize', { name: r.file.name })
          : t('NLSCommonFilePickerRejectType', { name: r.file.name }),
      )
      setRejectErrors(messages)
    },
    [t],
  )

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => {
      const removed = prev[index]
      if (removed) {
        uploadedFileIdsRef.current.delete(removed)
      }
      return prev.filter((_, i) => i !== index)
    })
    setError(null)
  }, [])

  const handleSubmit = async () => {
    const trimmedNote = note.trim()
    if (!trimmedNote && files.length === 0) {
      return
    }
    if (files.length > 5) {
      setError(t('NLSFamilyHandInDialogTooManyFiles'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const pendingFiles = files.filter((f) => !uploadedFileIdsRef.current.has(f))

      if (pendingFiles.length > 0) {
        setUploadProgress({ completed: 0, total: pendingFiles.length })
        const newIds = await uploadHomeworkFiles({
          workspaceId,
          checklistId,
          learnerId,
          files: pendingFiles,
          onProgress: (completed, total) => {
            setUploadProgress({ completed, total })
          },
        })
        pendingFiles.forEach((file, index) => {
          const fid = newIds[index]
          if (fid) {
            uploadedFileIdsRef.current.set(file, fid)
          }
        })
      }

      const fileIds = files
        .map((f) => uploadedFileIdsRef.current.get(f))
        .filter((id): id is string => typeof id === 'string' && id.length > 0)

      await submitEduHomework({
        workspaceId,
        checklistId,
        learnerId,
        source: 'WEB',
        note: trimmedNote || undefined,
        fileIds,
      })

      handleReset()
      onSuccess()
      onClose()
    } catch (err: unknown) {
      if (isTransientOutboxError(err)) {
        try {
          const uploadedFiles: File[] = []
          const uploadedIds: string[] = []
          const pendingFiles: File[] = []

          files.forEach((f) => {
            const fid = uploadedFileIdsRef.current.get(f)
            if (fid) {
              uploadedFiles.push(f)
              uploadedIds.push(fid)
            } else {
              pendingFiles.push(f)
            }
          })

          const orderedFiles = [...uploadedFiles, ...pendingFiles]

          await queueMemberHomeworkSubmit(
            {
              workspaceId,
              checklistId,
              learnerId,
              source: 'WEB',
              note: trimmedNote || undefined,
            },
            orderedFiles.map((f) => ({
              name: f.name,
              type: f.type,
              blob: f,
            })),
            {
              uid: currentUser?.uid,
              workspaceId,
            },
            uploadedIds,
          )
          setSavedOffline(true)
          setIsSubmitting(false)
          setUploadProgress(null)
          onSuccess()
          return
        } catch (queueErr) {
          setIsSubmitting(false)
          setUploadProgress(null)
          setError(mapEduServerError(queueErr, t))
          return
        }
      }
      setIsSubmitting(false)
      setUploadProgress(null)
      setError(mapEduServerError(err, t))
    }
  }

  const isSubmitDisabled = !note.trim() && files.length === 0

  const uploadProgressLabel = uploadProgress
    ? t('NLSFamilyHandInDialogUploading', {
        completed: uploadProgress.completed,
        total: uploadProgress.total,
      })
    : null

  const actions: AppDialogAction[] = savedOffline
    ? [
        {
          label: t('NLSFamilyHandInDialogClose'),
          variant: 'primary',
          onClick: handleCancel,
        },
      ]
    : [
        {
          label: t('NLSFamilyHandInDialogCancel'),
          variant: 'ghost',
          disabled: isSubmitting,
          onClick: handleCancel,
        },
        {
          label: isSubmitting
            ? uploadProgressLabel || t('NLSFamilyHandInDialogSubmitting')
            : t('NLSFamilyHandInDialogSubmit'),
          variant: 'primary',
          disabled: isSubmitDisabled || isSubmitting,
          loading: isSubmitting,
          onClick: handleSubmit,
        },
      ]

  return (
    <AppDialog
      open={open}
      title={title || t('NLSFamilyHandInDialogTitle')}
      onDismiss={isSubmitting ? undefined : handleCancel}
      actions={actions}
      size="md"
    >
      <div className="familyHandInDialog">
        {savedOffline && (
          <div className="familyHandInDialog__savedOffline" role="status">
            {t('NLSFamilyHandInDialogSavedOffline')}
          </div>
        )}
        {error && <div className="familyHandInDialog__error">{error}</div>}
        {rejectErrors.map((msg, idx) => (
          <div key={`reject_${idx}`} className="familyHandInDialog__error">
            {msg}
          </div>
        ))}

        <div className="familyHandInDialog__field">
          <label className="familyHandInDialog__label">{t('NLSFamilyHandInDialogNoteLabel')}</label>
          <Textarea
            value={note}
            onChange={setNote}
            maxLength={2000}
            disabled={isSubmitting}
            placeholder={t('NLSFamilyHandInDialogNotePlaceholder')}
          />
        </div>

        <div className="familyHandInDialog__field">
          <label className="familyHandInDialog__label">{t('NLSFamilyHandInDialogFilesLabel')}</label>
          <FilePicker
            accept="image/*,application/pdf"
            maxBytes={STORAGE_MAX_FILE_BYTES}
            multiple={true}
            disabled={isSubmitting || files.length >= 5}
            onFiles={handleFiles}
            onReject={handleReject}
          />

          {files.length > 0 && (
            <div className="familyHandInDialog__files">
              {files.map((file, idx) => (
                <FileRow
                  key={`${file.name}_${file.size}_${idx}`}
                  name={file.name}
                  sizeBytes={file.size}
                  onRemove={isSubmitting ? undefined : () => handleRemoveFile(idx)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppDialog>
  )
}
